/** Reading the logger's lap analysis document.
 *
 * The document is the logger's (`gt7-datalogger-lap-analysis`, stored byte for
 * byte by the service) and its one rule is that a missing key means "not known
 * or not done", never zero. So every field below is optional, and nothing here
 * fills a gap with a number: a corner the logger said nothing about is a row of
 * dashes, not a row of zeroes that reads as a perfect corner.
 *
 * A lap's gap to the reference is split along the lap: each corner's own time,
 * the straight leading into it (`approach`), and the run from the last corner
 * to the line (`to_line`). Those pieces add up to the lap's `time_vs_ref`, so
 * the view can say where the time went without inventing anything. A lap the
 * logger could not line up with the reference (`aligned: false`) has pieces
 * that do not add up, and its differences are not shown at all.
 */

export interface AnalysisDelta {
  length_m?: number;
  time_ms?: number;
  time_vs_ref?: number;
  max_speed?: number;
}

export interface AnalysisCorner {
  n: number;
  direction?: string;
  entry_m?: number;
  apex_m?: number;
  exit_m?: number;
  angle_deg?: number;
}

export interface AnalysisLapCorner {
  n: number;
  time_ms?: number;
  time_vs_ref?: number;
  speed?: {
    entry?: number;
    min?: number;
    exit?: number;
    min_vs_ref?: number;
    exit_vs_ref?: number;
    min_from_apex_m?: number;
    min_gear?: number;
    /** The gear GT7 last suggested on the way in (#108), and whether the
     *  lap was in a higher one at its slowest point. Absent on a recording
     *  without suggestions. */
    suggested_gear?: number;
    gear_high?: boolean;
  };
  braking?: {
    none?: boolean;
    on_from_apex_m?: number;
    on_vs_ref?: number;
    length_m?: number;
    peak_pct?: number;
    lockups?: number;
  };
  throttle?: {
    flat?: boolean;
    on_from_apex_m?: number;
    full_from_apex_m?: number;
    wheelspin?: number;
  };
  approach?: AnalysisDelta;
}

export interface TyreTemp {
  avg?: number;
  max?: number;
  end?: number;
}

export interface AnalysisLap {
  number: number;
  time_ms?: number;
  time_vs_ref?: number;
  counts_for_best?: boolean;
  clean?: boolean;
  aligned?: boolean;
  fuel?: { start?: number; end?: number; used?: number };
  pedals?: { full_throttle_pct?: number; full_brake_pct?: number; coasting_pct?: number };
  aids?: { tcs_pct?: number; asm_pct?: number };
  max_speed?: number;
  tyre_temp?: Partial<Record<'fl' | 'fr' | 'rl' | 'rr', TyreTemp>>;
  events?: { wheelspin?: number; lockup?: number; bottoming?: number };
  off_track?: number;
  corners?: AnalysisLapCorner[];
  to_line?: AnalysisDelta;
}

export interface Spread {
  laps?: number;
  std?: number;
  spread?: number;
}

export interface LapAnalysis {
  format: string;
  version: number;
  compiled_at?: string;
  app_version?: string;
  circuit?: { name?: string; lap_length_m?: number; corners?: string };
  reference?: { number?: number; time_ms?: number; why?: string };
  corners?: AnalysisCorner[];
  laps?: AnalysisLap[];
  consistency?: {
    lap_time?: { laps?: number; best_ms?: number; median_ms?: number; std_ms?: number; pct?: number };
    corners?: {
      by_corner?: Array<{ n: number; brake_on?: Spread; min_speed?: Spread; time?: Spread }>;
    };
  };
}

/** The analysis's own record of one lap, by the number the lap table shows. */
export function analysisLap(document: LapAnalysis | null, lap: number | null): AnalysisLap | null {
  if (!document || lap === null) return null;
  return document.laps?.find((row) => row.number === lap) ?? null;
}

/** Whether a lap's differences against the reference can be believed. */
export function comparable(lap: AnalysisLap | null): boolean {
  return lap !== null && lap.aligned !== false;
}

export interface CornerRow {
  n: number;
  direction: string | null;
  angle: number | null;
  /** This lap's figures at the corner, when the logger measured them. */
  lap: AnalysisLapCorner | null;
  /** The corner plus the straight into it, against the reference. */
  lostMs: number | null;
  /** How much this corner's time moves from lap to lap, σ in ms. */
  timeStd: number | null;
}

/** One row per corner of the circuit, in order, whatever this lap measured. */
export function cornerRows(document: LapAnalysis | null, lap: AnalysisLap | null): CornerRow[] {
  if (!document) return [];
  const measured = new Map((lap?.corners ?? []).map((corner) => [corner.n, corner]));
  const steady = new Map(
    (document.consistency?.corners?.by_corner ?? []).map((row) => [row.n, row.time?.std]),
  );
  const known = comparable(lap);

  return (document.corners ?? []).map((corner) => {
    const own = measured.get(corner.n) ?? null;
    return {
      n: corner.n,
      direction: corner.direction ?? null,
      angle: corner.angle_deg ?? null,
      lap: own,
      lostMs: known ? sectionLoss(own) : null,
      timeStd: steady.get(corner.n) ?? null,
    };
  });
}

/** A corner's time and its approach together — the straight before a corner is
 *  where a bad exit from the previous one, or an early lift, shows up. */
function sectionLoss(corner: AnalysisLapCorner | null): number | null {
  if (!corner) return null;
  const parts = [corner.time_vs_ref, corner.approach?.time_vs_ref].filter(
    (value): value is number => typeof value === 'number',
  );
  return parts.length === 0 ? null : parts.reduce((sum, value) => sum + value, 0);
}

/** The corners where most time went, worst first, only those that lost any. */
export function worstCorners(rows: readonly CornerRow[], limit = 3, floorMs = 20): CornerRow[] {
  return rows
    .filter((row) => row.lostMs !== null && row.lostMs > floorMs)
    .sort((a, b) => (b.lostMs ?? 0) - (a.lostMs ?? 0))
    .slice(0, limit);
}

/** The largest single loss or gain, which is what the bars are scaled to. */
export function lossScale(rows: readonly CornerRow[]): number {
  return rows.reduce((most, row) => Math.max(most, Math.abs(row.lostMs ?? 0)), 0);
}

/** `T3 R` — a corner as a driver names it. */
export function cornerName(row: Pick<CornerRow, 'n' | 'direction'>): string {
  return row.direction ? `T${row.n} ${row.direction}` : `T${row.n}`;
}
